import {
    ChargingStation,
    TransactionsDetailData,
    Value,
} from 'services/centralServerService/data-contracts';
import { action, computed, makeObservable, observable } from 'mobx';

import AbstractStore from '../abstractStore';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { TransactionOtp } from 'services/centralServerService/Api';
import { centralServerService } from 'services/centralServerService';
import { el } from 'assets/localizations/el';
import { en } from 'assets/localizations/en';
import { ro } from 'assets/localizations/ro';
import moment from 'moment';
import { Constants } from 'services/constants';
import { getSessionCurrency } from 'utils/currency';
import { v4 as uuidv4 } from 'uuid';

export enum ActiveView {
    Otp,
    Loading,
    Details,
}

interface LineChartDatasource {
    labels: string[];
    datasets: {
        label: string;
        color:
        | 'primary'
        | 'secondary'
        | 'info'
        | 'success'
        | 'warning'
        | 'error'
        | 'light'
        | 'dark';
        data: Array<number | null>;
    }[];
}

export enum Languages {
    en = 'en',
    el = 'el',
    ro = 'ro',
}

export const normalizeLanguageCode = (
    language?: string | null,
): Languages | undefined => {
    const normalized = language
        ?.trim()
        .replace('_', '-')
        .split('-')[0]
        .toLowerCase();
    return Object.values(Languages).includes(normalized as Languages)
        ? normalized as Languages
        : undefined;
};

class Store extends AbstractStore {
    private deviceIdKey = 'deviceId';
    private tokenKey = 'token';
    private languageKey = 'language';
    private languageChoiceContextKey = 'languageChoiceContext';

    public deviceId: string | null = localStorage.getItem(this.deviceIdKey);

    constructor() {
        super();
        makeObservable(this, {
            session: observable,
            setSession: action,
            getTransactionsOtp: action,
            activeView: observable,
            setActiveView: action,
            token: observable,
            setToken: action,
            count: observable,
            setCount: action,
            deviceId: observable,
            _setDeviceId: action,
            tenantLogo: observable,
            setTenantLogo: action,
            timer: observable,
            setTimer: action,
            language: observable,
            setLanguage: action,
            applySuggestedLanguage: action,
            setLanguageContext: action,
            setAndRememberLanguage: action,
            localizations: computed,
        });
        this._setDeviceId(this.deviceId || uuidv4());
    }

    public language: Languages = this.normalizeLanguage(
        localStorage.getItem(this.languageKey),
    );

    // Only a choice made during this browser flow outranks a fresh terminal locale.
    private languageWasChosenByUser = false;
    private languageContext: string | null = null;

    public get localizations() {
        const language = this.normalizeLanguage(this.language);
        if (language === Languages.ro) {
            return ro;
        }
        if (language === Languages.el) {
            return el;
        } else {
            return en;
        }
    }

    public L(key: LocalizationKeys, ...args: (string | number)[]) {
        const ret = this.localizations[key] || key;
        if (args.length > 0) {
            return ret.replace(/{(\d+)}/g, (match, number) => {
                return typeof args[number] != 'undefined'
                    ? `${args[number]}`
                    : match;
            });
        }
        return ret;
    }

    private normalizeLanguage(language?: string | null): Languages {
        return normalizeLanguageCode(language) || Languages.el;
    }

    public setLanguage = (language: Languages | string) => {
        // console.log('setLanguage', language);
        this.language = this.normalizeLanguage(language);
    };

    // For locales suggested by the payment terminal, via the URL or the session response.
    public applySuggestedLanguage = (language?: string | null) => {
        const normalized = normalizeLanguageCode(language);
        if (this.languageWasChosenByUser || !normalized) {
            return;
        }
        this.language = normalized;
    };

    public setLanguageContext = (context?: string | null) => {
        this.languageContext = context?.trim() || null;
        this.languageWasChosenByUser = !!this.languageContext &&
            localStorage.getItem(this.languageChoiceContextKey) === this.languageContext;
    };

    public setAndRememberLanguage = (language: Languages) => {
        localStorage.setItem(this.languageKey, language);
        if (this.languageContext) {
            localStorage.setItem(this.languageChoiceContextKey, this.languageContext);
        } else {
            localStorage.removeItem(this.languageChoiceContextKey);
        }
        this.languageWasChosenByUser = true;
        this.setLanguage(language);
    };

    public timer: number = 0;

    public tenantLogo: string | null = null;

    public token: string | null = localStorage.getItem(this.tokenKey);

    public count: number = 1;
    public activeView: ActiveView = ActiveView.Loading;
    public session:
        | (TransactionsDetailData & { chargeBox: ChargingStation } & {
            paymentTerminalLocale: Languages | string;
        })
        | undefined;

    public setTimer = (timer: number) => {
        this.timer = timer;
    };

    public setCount = (count: number) => {
        this.count = count;
    };

    public setTenantLogo = (logo: string) => {
        this.tenantLogo = logo;
    };

    public setToken = (token: string | null) => {
        if (token) {
            localStorage.setItem(this.tokenKey, token);
        } else {
            localStorage.removeItem(this.tokenKey);
        }
        this.token = token;
    };

    public _setDeviceId = (deviceId: string) => {
        localStorage.setItem(this.deviceIdKey, deviceId);
        this.deviceId = deviceId;
    };

    public setSession = (
        otp:
            | (TransactionsDetailData & { chargeBox: ChargingStation } & {
                paymentTerminalLocale: Languages | string;
            })
            | undefined,
    ) => {
        this.applySuggestedLanguage(otp?.paymentTerminalLocale);
        this.session = otp;
    };

    public setActiveView(view: ActiveView) {
        this.activeView = view;
    }

    public getTransactionsOtp = async (
        tenantId: string,
        sessionId: string,
        payload: TransactionOtp,
    ) => {
        const result = await centralServerService.getTransactionsOtp(
            tenantId,
            sessionId,
            payload,
        );
        // console.log('result', result);
        this.setSession(result);
    };

    public stopTransactionOtp = async (
        tenantId: string,
        sessionId: string,
        payload: TransactionOtp,
    ) => {
        await centralServerService.stopTransactionOtp(
            tenantId,
            sessionId,
            payload,
        );

        localStorage.setItem(`${tenantId}-${sessionId}`, 'stopped');
        this.setSession(undefined);
    };

    get ChartViewOfConsumptionsWh(): LineChartDatasource | undefined {
        return this.chartMapper(['cumulatedConsumptionWh'], ['info'], {
            cumulatedConsumptionWh: LocalizationKeys.CumulativeConsumption,
        });
    }

    get ChartViewOfCumulativePrice(): LineChartDatasource | undefined {
        return this.chartMapper(['cumulatedRoundedAmountInclVat'], ['success'], {
            cumulatedRoundedAmountInclVat: LocalizationKeys.CumulativePrice,
        });
    }

    chartMapper = (
        datakeys: (keyof Value)[],
        color: (
            | 'primary'
            | 'secondary'
            | 'info'
            | 'success'
            | 'warning'
            | 'error'
            | 'light'
            | 'dark'
        )[],
        legend: { [key: string]: LocalizationKeys },
    ) => {
        const tmp = {} as LineChartDatasource;
        const data = this.session?.values;
        if (!data || data.length < 1) return;
        tmp.labels = data.map((v) => moment(v.endedAt).format('HH:mm'));
        tmp.datasets = datakeys.map((key: keyof Value, i) => {
            const labelArgs =
                key === 'cumulatedRoundedAmountInclVat'
                    ? [
                        getSessionCurrency(
                            this.session || {
                                priceUnit: Constants.DEFAULT_CURRENCY_CODE,
                            },
                        ),
                    ]
                    : [];
            return {
                label: this.L(legend?.[key] || key, ...labelArgs),
                color: color[i],
                data: data.map((v) => {
                    const value = +v?.[key];
                    return isNaN(value) ? null : value;
                }),
            } as any;
        });
        return tmp;
    };
}
export default Store;
export { Store };
